/**
 * Fase 3 — Gastos y adelantos.
 *
 * Arriba de todo, y siempre a la vista, el SALDO A RENDIR de cada chofer:
 * cuánta plata de la empresa tiene sin justificar. Ése es el número que el
 * día 30 se le descuenta del 15%.
 *
 * Va arriba a propósito. El riesgo R2 del Documento 1 es la discusión con el
 * chofer el día de cobrar; la forma de evitarla es que ese número esté a la
 * vista todo el mes, no que aparezca de sorpresa al final. Por eso también
 * se muestra el detalle: qué adelantos recibió y qué gastos le rindieron.
 *
 * El cálculo lo hace el SERVIDOR, con la misma regla que después usa la
 * liquidación. Esta pantalla no calcula plata: la muestra.
 */

import { formatearPesos, formatearFecha } from './formato.js';

const $ = (sel, dentro = document) => dentro.querySelector(sel);

function alerta(tipo, texto) {
  const simbolos = { critico: '!', aviso: '!', bien: '✓' };
  const div = document.createElement('div');
  div.className = `alerta ${tipo}`;
  div.setAttribute('role', tipo === 'critico' ? 'alert' : 'status');
  div.innerHTML = `<span class="simbolo" aria-hidden="true">${simbolos[tipo] ?? '·'}</span><span></span>`;
  div.lastElementChild.textContent = texto;
  return div;
}

/** Los pesos que se escriben en pantalla, a centavos enteros. */
function aCentavos(texto) {
  const limpio = String(texto ?? '').replace(/\./g, '').replace(',', '.').trim();
  if (limpio === '') return '0';
  if (!/^\d+(\.\d{1,2})?$/.test(limpio)) return null;
  const [entero, decimal = ''] = limpio.split('.');
  return `${entero}${decimal.padEnd(2, '0')}`;
}

const periodoActual = () => {
  const h = new Date();
  return `${h.getFullYear()}-${String(h.getMonth() + 1).padStart(2, '0')}`;
};

const PAGADO_CON = {
  adelanto_al_chofer: 'Con el adelanto del chofer',
  caja_empresa: 'Caja de la empresa',
  tarjeta_empresa: 'Tarjeta de la empresa',
  cuenta_corriente_proveedor: 'Cuenta corriente del proveedor',
};

const opciones = (lista, etiqueta, elegido) =>
  lista.map((f) => `<option value="${f.id}" ${f.id === elegido ? 'selected' : ''}>${
    etiqueta(f)}</option>`).join('');

const nombreChofer = (c) => `${c.nombre} ${c.apellido}`;
const nombreCamion = (c) => `${c.patente}${c.marca ? ' — ' + c.marca : ''}`;

async function traerListas(pedir) {
  const [tipos, choferes, camiones, proveedores, viajes] = await Promise.all([
    pedir('/api/tipos-gasto'), pedir('/api/choferes'), pedir('/api/camiones'),
    pedir('/api/proveedores'), pedir('/api/viajes'),
  ]);
  const vehiculos = camiones.ok ? camiones.cuerpo.camiones : [];
  return {
    tipos: tipos.ok ? tipos.cuerpo['tipos-gasto'] : [],
    choferes: choferes.ok ? choferes.cuerpo.choferes : [],
    // Un gasto se imputa a un vehículo con motor o a un acoplado: los dos
    // gastan (el acoplado tiene cubiertas, técnica y seguro propios).
    camiones: vehiculos,
    proveedores: proveedores.ok ? proveedores.cuerpo.proveedores : [],
    viajes: viajes.ok ? viajes.cuerpo.viajes : [],
  };
}

// =======================================================================
// El saldo a rendir: el número que importa
// =======================================================================
function dibujarSaldos(caja, datos) {
  const filas = datos.choferes ?? [];
  if (filas.length === 0) {
    caja.innerHTML = '<p style="color:var(--texto-suave);margin:0">No hay choferes cargados.</p>';
    return;
  }

  const renglon = (c) => {
    const saldo = BigInt(c.saldo_cents);
    // Debe rendir = tiene plata de la empresa sin justificar. Negativo = puso
    // de su bolsillo y la empresa le debe. Cero = está al día.
    const color = saldo > 0n ? 'var(--grave)' : saldo < 0n ? 'var(--critico)' : 'var(--bien-texto)';
    const queSignifica = saldo > 0n
      ? 'Tiene que rendir esto o se le descuenta del 15%'
      : saldo < 0n ? 'Puso plata de su bolsillo: la empresa le debe'
      : 'Al día: rindió todo lo que recibió';
    const noRinden = (c.gastos_que_no_rinden ?? []);
    const totalNoRinde = noRinden.reduce((s, g) => s + BigInt(g.total_cents), 0n);
    return `<tr data-chofer="${c.chofer_id}">
      <td>${c.chofer_nombre}</td>
      <td class="plata">${formatearPesos(c.adelantos_recibidos_cents)}</td>
      <td class="plata">${formatearPesos(c.rendido_cents)}</td>
      <td class="plata" data-rol="saldo" style="color:${color};font-weight:600">${
        formatearPesos(c.saldo_cents)}</td>
      <td style="font-size:12.5px;color:var(--texto-suave)">${queSignifica}${
        totalNoRinde > 0n
          ? `<br>Además gastó ${formatearPesos(totalNoRinde.toString())} en cosas que ` +
            'no rinden (comida, viáticos): eso es suyo.'
          : ''}</td>
    </tr>`;
  };

  caja.innerHTML = `
    <div class="tabla-scroll"><table class="datos">
      <thead><tr>
        <th>Chofer</th><th class="plata">Adelantos</th><th class="plata">Rindió</th>
        <th class="plata">Saldo a rendir</th><th>Qué significa</th>
      </tr></thead>
      <tbody>${filas.map(renglon).join('')}</tbody>
    </table></div>`;
}

// =======================================================================
// La pantalla
// =======================================================================
export async function pantallaDeGastos(contenedor, { pedir }) {
  contenedor.innerHTML = '';
  const listas = await traerListas(pedir);

  if (listas.tipos.length === 0) {
    contenedor.appendChild(alerta('aviso',
      'Antes de cargar gastos hace falta al menos un tipo de gasto. Se cargan en «Tipos de gasto».'));
    return;
  }

  const marco = document.createElement('div');
  marco.innerHTML = `
    <div class="tarjeta" style="margin-bottom:16px">
      <div style="display:flex;justify-content:space-between;align-items:center;gap:14px;flex-wrap:wrap">
        <div>
          <h2 style="font-size:16px;margin:0 0 3px">Saldo a rendir por chofer</h2>
          <p style="color:var(--texto-suave);font-size:12.5px;margin:0">
            Plata de la empresa que todavía no justificó con comprobantes. Es lo que se le
            descuenta del 15% al cerrar el mes.</p>
        </div>
        <div class="campo" style="margin:0">
          <label for="g-periodo">Mes</label>
          <input type="month" id="g-periodo" value="${periodoActual()}">
        </div>
      </div>
      <div data-rol="saldos" style="margin-top:14px"></div>
    </div>

    <div class="tarjeta" style="margin-bottom:16px">
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:12px">
        <h2 style="font-size:16px;margin:0">Gastos</h2>
        <button type="button" class="boton principal" data-accion="nuevo-gasto">Nuevo gasto</button>
      </div>
      <div data-rol="form-gasto" hidden></div>
      <div data-rol="tabla-gastos"></div>
    </div>

    <div class="tarjeta">
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:12px">
        <h2 style="font-size:16px;margin:0">Adelantos</h2>
        <button type="button" class="boton principal" data-accion="nuevo-adelanto">Nuevo adelanto</button>
      </div>
      <div data-rol="form-adelanto" hidden></div>
      <div data-rol="tabla-adelantos"></div>
    </div>`;
  contenedor.appendChild(marco);

  const cajaSaldos = $('[data-rol="saldos"]', marco);
  const cajaGastos = $('[data-rol="tabla-gastos"]', marco);
  const cajaAdelantos = $('[data-rol="tabla-adelantos"]', marco);
  const formGasto = $('[data-rol="form-gasto"]', marco);
  const formAdelanto = $('[data-rol="form-adelanto"]', marco);
  const campoPeriodo = $('#g-periodo', marco);

  const periodo = () => campoPeriodo.value || periodoActual();

  async function refrescar() {
    const [saldos, gastos, adelantos] = await Promise.all([
      pedir(`/api/rendicion?periodo=${periodo()}`),
      pedir(`/api/gastos?periodo=${periodo()}`),
      pedir(`/api/adelantos?periodo=${periodo()}`),
    ]);
    if (saldos.ok) dibujarSaldos(cajaSaldos, saldos.cuerpo);
    if (gastos.ok) dibujarGastos(gastos.cuerpo.gastos ?? []);
    if (adelantos.ok) dibujarAdelantos(adelantos.cuerpo.adelantos ?? []);
  }

  function dibujarGastos(filas) {
    if (filas.length === 0) {
      cajaGastos.innerHTML =
        `<p style="color:var(--texto-suave);text-align:center;padding:20px 0;margin:0">
           No hay gastos cargados en este mes.</p>`;
      return;
    }
    cajaGastos.innerHTML = `
      <div class="tabla-scroll"><table class="datos">
        <thead><tr>
          <th>Fecha</th><th>Tipo</th><th>Proveedor</th><th>Viaje / camión</th>
          <th>Chofer</th><th>Pagado con</th><th class="plata">Total</th><th>Ticket</th><th></th>
        </tr></thead>
        <tbody>${filas.map((g) => `
          <tr data-id="${g.id}" style="${g.anulado ? 'opacity:.55' : ''}">
            <td>${formatearFecha(g.fecha)}</td>
            <td>${g.tipo_gasto_nombre}${g.paga_empresa ? '' :
              '<br><small style="color:var(--texto-suave)">lo paga el chofer</small>'}</td>
            <td>${g.proveedor_nombre ?? '—'}</td>
            <td>${g.viaje_numero ?? g.camion_patente ?? '—'}</td>
            <td>${g.chofer_nombre ?? '—'}</td>
            <td>${PAGADO_CON[g.pagado_con] ?? g.pagado_con}</td>
            <td class="plata">${formatearPesos(g.total_cents)}</td>
            <td>${g.adjunto_ruta
              ? `<a class="boton" href="/api/gastos/${g.id}/adjunto" target="_blank" rel="noopener">Ver ticket</a>`
              : '<span style="color:var(--texto-suave);font-size:12.5px">sin ticket</span>'}</td>
            <td>${g.anulado
              ? '<span class="etiqueta critico">Anulado</span>'
              : `<button type="button" class="boton" data-anular-gasto="${g.id}">Anular</button>`}</td>
          </tr>`).join('')}</tbody>
      </table></div>`;
    for (const b of cajaGastos.querySelectorAll('[data-anular-gasto]')) {
      b.addEventListener('click', () => anular('gastos', b.dataset.anularGasto, 'el gasto'));
    }
  }

  function dibujarAdelantos(filas) {
    if (filas.length === 0) {
      cajaAdelantos.innerHTML =
        `<p style="color:var(--texto-suave);text-align:center;padding:20px 0;margin:0">
           No hay adelantos cargados en este mes.</p>`;
      return;
    }
    cajaAdelantos.innerHTML = `
      <div class="tabla-scroll"><table class="datos">
        <thead><tr>
          <th>Fecha</th><th>Chofer</th><th>Medio</th><th>Concepto</th>
          <th class="plata">Importe</th><th></th>
        </tr></thead>
        <tbody>${filas.map((a) => `
          <tr data-id="${a.id}" style="${a.anulado ? 'opacity:.55' : ''}">
            <td>${formatearFecha(a.fecha)}</td>
            <td>${a.chofer_nombre}</td>
            <td>${a.medio}</td>
            <td>${a.concepto ?? '—'}</td>
            <td class="plata">${formatearPesos(a.importe_cents)}</td>
            <td>${a.anulado
              ? '<span class="etiqueta critico">Anulado</span>'
              : `<button type="button" class="boton" data-anular-adelanto="${a.id}">Anular</button>`}</td>
          </tr>`).join('')}</tbody>
      </table></div>`;
    for (const b of cajaAdelantos.querySelectorAll('[data-anular-adelanto]')) {
      b.addEventListener('click', () => anular('adelantos', b.dataset.anularAdelanto, 'el adelanto'));
    }
  }

  async function anular(recurso, id, comoSeLlama) {
    // Anular NO borra: deja el registro y el motivo. Por eso se pide el
    // motivo y no se puede dejar vacío.
    const motivo = prompt(`¿Por qué se anula ${comoSeLlama}? Queda registrado.`);
    if (motivo === null || motivo.trim() === '') return;
    const r = await pedir(`/api/${recurso}/${id}/anular`,
      { method: 'POST', body: JSON.stringify({ motivo: motivo.trim() }) });
    if (!r.ok) { alert(r.cuerpo?.error ?? 'No se pudo anular.'); return; }
    await refrescar();
  }

  // --- El formulario de gasto ------------------------------------------
  function abrirFormularioDeGasto() {
    formGasto.hidden = false;
    formGasto.innerHTML = `
      <div data-rol="errores"></div>
      <form method="post" action="/api/gastos" novalidate
            style="border:1px solid var(--borde);border-radius:8px;padding:14px;margin-bottom:14px">
        <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(210px,1fr));gap:0 16px">
          <div class="campo"><label for="ga-fecha">Fecha *</label>
            <input type="date" id="ga-fecha" name="fecha"></div>
          <div class="campo"><label for="ga-tipo">Tipo de gasto *</label>
            <select id="ga-tipo" name="tipo_gasto_id"><option value="">— elegir —</option>
              ${opciones(listas.tipos, (t) => t.nombre)}</select>
            <div class="ayuda" data-rol="aviso-tipo"></div></div>
          <div class="campo"><label for="ga-viaje">Viaje</label>
            <select id="ga-viaje" name="viaje_id"><option value="">— ninguno —</option>
              ${opciones(listas.viajes, (v) => `${v.numero} · ${v.origen} → ${v.destino}`)}</select>
            <div class="ayuda">Al elegirlo, el camión sale del viaje.</div></div>
          <div class="campo"><label for="ga-camion">Camión o acoplado</label>
            <select id="ga-camion" name="camion_id"><option value="">— ninguno —</option>
              ${opciones(listas.camiones, nombreCamion)}</select>
            <div class="ayuda">Hace falta uno de los dos: el viaje o el vehículo.</div></div>
          <div class="campo"><label for="ga-pagado">Pagado con *</label>
            <select id="ga-pagado" name="pagado_con">
              ${Object.entries(PAGADO_CON).map(([v, t]) =>
                `<option value="${v}" ${v === 'caja_empresa' ? 'selected' : ''}>${t}</option>`).join('')}
            </select></div>
          <div class="campo"><label for="ga-chofer">Chofer</label>
            <select id="ga-chofer" name="chofer_id"><option value="">— ninguno —</option>
              ${opciones(listas.choferes, nombreChofer)}</select>
            <div class="ayuda" data-rol="aviso-chofer"></div></div>
          <div class="campo"><label for="ga-proveedor">Proveedor</label>
            <select id="ga-proveedor" name="proveedor_id"><option value="">— ninguno —</option>
              ${opciones(listas.proveedores, (p) => p.razon_social)}</select></div>
          <div class="campo"><label for="ga-neto">Importe neto *</label>
            <input type="text" id="ga-neto" name="importe_neto_cents" inputmode="decimal">
            <div class="ayuda">En pesos, sin IVA.</div></div>
          <div class="campo"><label for="ga-iva">IVA</label>
            <input type="text" id="ga-iva" name="iva_cents" inputmode="decimal"></div>
          <div class="campo"><label for="ga-otros">Otros impuestos que NO se recuperan</label>
            <input type="text" id="ga-otros" name="otros_impuestos_no_recuperables_cents" inputmode="decimal">
            <div class="ayuda">Ésos sí son costo real del viaje.</div></div>
          <div class="campo"><label for="ga-comp">Número de comprobante</label>
            <input type="text" id="ga-comp" name="comprobante_numero"></div>
          <div class="campo"><label for="ga-litros">Litros</label>
            <input type="text" id="ga-litros" name="litros" inputmode="decimal">
            <div class="ayuda">Sólo para combustible.</div></div>
          <div class="campo"><label for="ga-obs">Observaciones</label>
            <input type="text" id="ga-obs" name="observaciones"></div>
          <div class="campo"><label for="ga-ticket">Foto del ticket</label>
            <input type="file" id="ga-ticket" name="ticket"
                   accept="image/jpeg,image/png,image/webp,application/pdf" capture="environment">
            <div class="ayuda">Sacale una foto con el teléfono. Se acepta JPG, PNG, WEBP o PDF,
              hasta 8 MB.</div></div>
        </div>
        <p style="margin:8px 0 0;font-size:13px">Total:
          <b data-rol="total-gasto">$ 0,00</b>
          <span style="color:var(--texto-suave)">= neto + IVA + otros impuestos. Lo calcula el sistema.</span></p>
        <div style="display:flex;gap:10px;margin-top:12px">
          <button type="submit" class="boton principal">Guardar gasto</button>
          <button type="button" class="boton" data-accion="cancelar-gasto">Cancelar</button>
        </div>
      </form>`;

    const form = $('form', formGasto);
    const avisoTipo = $('[data-rol="aviso-tipo"]', formGasto);
    const avisoChofer = $('[data-rol="aviso-chofer"]', formGasto);
    const total = $('[data-rol="total-gasto"]', formGasto);

    function recalcular() {
      const suma = ['ga-neto', 'ga-iva', 'ga-otros']
        .map((id) => aCentavos($(`#${id}`, form).value))
        .reduce((s, v) => (v === null ? s : s + BigInt(v)), 0n);
      total.textContent = formatearPesos(suma.toString());

      const tipo = listas.tipos.find((t) => t.id === $('#ga-tipo', form).value);
      avisoTipo.textContent = !tipo ? ''
        : tipo.paga_empresa
          ? 'Lo paga la empresa: si se pagó con el adelanto, le rinde al chofer.'
          : 'Lo paga el CHOFER: aunque lo pague con el adelanto, NO le rinde.';

      avisoChofer.textContent = $('#ga-pagado', form).value === 'adelanto_al_chofer'
        ? 'Obligatorio: ese gasto le rinde a este chofer.' : '';
    }
    form.addEventListener('input', recalcular);
    form.addEventListener('change', recalcular);
    recalcular();

    $('[data-accion="cancelar-gasto"]', form).addEventListener('click', () => {
      formGasto.hidden = true; formGasto.innerHTML = '';
    });
    form.addEventListener('submit', (e) => { e.preventDefault(); void guardarGasto(form, false); });
  }

  async function guardarGasto(form, confirmado) {
    const cajaErrores = $('[data-rol="errores"]', formGasto);
    cajaErrores.innerHTML = '';
    const datos = { confirmado };
    for (const campo of ['fecha', 'tipo_gasto_id', 'viaje_id', 'camion_id', 'chofer_id',
                         'proveedor_id', 'pagado_con', 'comprobante_numero', 'litros',
                         'observaciones']) {
      datos[campo] = form.elements[campo]?.value ?? '';
    }
    for (const campo of ['importe_neto_cents', 'iva_cents',
                         'otros_impuestos_no_recuperables_cents']) {
      datos[campo] = aCentavos(form.elements[campo]?.value) ?? '';
    }

    const r = await pedir('/api/gastos', { method: 'POST', body: JSON.stringify(datos) });

    if (r.cuerpo?.duplicado) {
      // No se prohíbe: puede haber dos cargas iguales el mismo día. Se
      // pregunta, que es lo que atrapa el error sin frenar el trabajo.
      const d = r.cuerpo.duplicado;
      const seguir = confirm(
        `${r.cuerpo.error}\n\nEl que ya está: ${d.tipo_gasto_nombre}, ` +
        `${formatearFecha(d.fecha)}, ${formatearPesos(d.total_cents)}.\n\n¿Guardar igual?`);
      if (seguir) await guardarGasto(form, true);
      return;
    }
    if (!r.ok) {
      for (const e of r.cuerpo?.errores ?? [{ rotulo: '', problema: r.cuerpo?.error ?? 'No se pudo guardar.' }]) {
        cajaErrores.appendChild(alerta('critico', e.rotulo ? `${e.rotulo}: ${e.problema}` : e.problema));
      }
      return;
    }
    // El gasto ya está guardado. El ticket se pega después, con el número de
    // gasto en la mano. Si la foto falla, el gasto NO se pierde: se avisa y
    // se puede volver a subir desde la lista.
    const archivo = form.elements['ticket']?.files?.[0];
    if (archivo) {
      const sobre = new FormData();
      sobre.append('archivo', archivo);
      const sub = await pedir(`/api/gastos/${r.cuerpo.id}/adjunto`,
        { method: 'POST', body: sobre });
      if (!sub.ok) {
        cajaErrores.appendChild(alerta('aviso',
          `El gasto se guardó, pero la foto no: ${sub.cuerpo?.error ?? 'no se pudo subir'}. ` +
          'Podés volver a intentarlo desde la lista.'));
        await refrescar();
        return;
      }
    }

    formGasto.hidden = true; formGasto.innerHTML = '';
    await refrescar();
  }

  // --- El formulario de adelanto ---------------------------------------
  function abrirFormularioDeAdelanto() {
    formAdelanto.hidden = false;
    formAdelanto.innerHTML = `
      <div data-rol="errores"></div>
      <form method="post" action="/api/adelantos" novalidate
            style="border:1px solid var(--borde);border-radius:8px;padding:14px;margin-bottom:14px">
        <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(210px,1fr));gap:0 16px">
          <div class="campo"><label for="ad-fecha">Fecha *</label>
            <input type="date" id="ad-fecha" name="fecha"></div>
          <div class="campo"><label for="ad-chofer">Chofer *</label>
            <select id="ad-chofer" name="chofer_id"><option value="">— elegir —</option>
              ${opciones(listas.choferes, nombreChofer)}</select></div>
          <div class="campo"><label for="ad-importe">Importe *</label>
            <input type="text" id="ad-importe" name="importe_cents" inputmode="decimal"></div>
          <div class="campo"><label for="ad-medio">Medio</label>
            <select id="ad-medio" name="medio">
              <option value="transferencia" selected>Transferencia</option>
              <option value="efectivo">Efectivo</option>
            </select></div>
          <div class="campo"><label for="ad-concepto">Concepto</label>
            <input type="text" id="ad-concepto" name="concepto"></div>
        </div>
        <p style="margin:8px 0 0;font-size:13px;color:var(--texto-suave)">
          Esto es plata de la empresa que el chofer administra, no un anticipo de su sueldo.
          Se cancela con comprobantes de gastos de la empresa.</p>
        <div style="display:flex;gap:10px;margin-top:12px">
          <button type="submit" class="boton principal">Guardar adelanto</button>
          <button type="button" class="boton" data-accion="cancelar-adelanto">Cancelar</button>
        </div>
      </form>`;

    const form = $('form', formAdelanto);
    $('[data-accion="cancelar-adelanto"]', form).addEventListener('click', () => {
      formAdelanto.hidden = true; formAdelanto.innerHTML = '';
    });
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const cajaErrores = $('[data-rol="errores"]', formAdelanto);
      cajaErrores.innerHTML = '';
      const datos = {
        fecha: form.elements['fecha'].value,
        chofer_id: form.elements['chofer_id'].value,
        importe_cents: aCentavos(form.elements['importe_cents'].value) ?? '',
        medio: form.elements['medio'].value,
        concepto: form.elements['concepto'].value,
      };
      const r = await pedir('/api/adelantos', { method: 'POST', body: JSON.stringify(datos) });
      if (!r.ok) {
        for (const er of r.cuerpo?.errores ?? [{ rotulo: '', problema: r.cuerpo?.error ?? 'No se pudo guardar.' }]) {
          cajaErrores.appendChild(alerta('critico', er.rotulo ? `${er.rotulo}: ${er.problema}` : er.problema));
        }
        return;
      }
      formAdelanto.hidden = true; formAdelanto.innerHTML = '';
      await refrescar();
    });
  }

  $('[data-accion="nuevo-gasto"]', marco).addEventListener('click', abrirFormularioDeGasto);
  $('[data-accion="nuevo-adelanto"]', marco).addEventListener('click', abrirFormularioDeAdelanto);
  campoPeriodo.addEventListener('change', () => { void refrescar(); });

  await refrescar();
}
